import { useId } from 'react';

/**
 * Ported from src/components/ui/TextField.tsx. `onChangeText` matches the React Native
 * signature so screens ported from the app do not have to unwrap events.
 */
export default function TextField({
  label,
  value,
  onChangeText,
  placeholder,
  multiline = false,
  type = 'text',
  error,
  hint,
  autoFocus,
  rows = 4,
}: {
  label?: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder?: string;
  multiline?: boolean;
  type?: 'text' | 'email' | 'password' | 'search';
  error?: string;
  /** Said before anything goes wrong — a rule the value must meet — and read with the field. */
  hint?: string;
  autoFocus?: boolean;
  rows?: number;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');
  const shared = {
    id,
    value,
    placeholder,
    autoFocus,
    'aria-invalid': error ? (true as const) : undefined,
    'aria-describedby': describedBy || undefined,
    className: `field ${error ? 'field-error' : ''}`.trim(),
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onChangeText(e.target.value),
  };
  return (
    <div className="field-wrap">
      {label && <label htmlFor={id} className="type-caption field-label">{label}</label>}
      {multiline ? <textarea {...shared} rows={rows} /> : <input {...shared} type={type} />}
      {hint && <p id={hintId} className="type-caption field-hint">{hint}</p>}
      {error && <p id={errorId} role="alert" className="type-caption field-error-text">{error}</p>}
    </div>
  );
}
